import { useLatest } from '@qfa/react-components';
import { useCallback, useMemo, useState } from 'react';
import { SetState } from 'types/props';

export interface DrawerState {
  open: boolean;
  locked: boolean;
}

export interface DrawerControls {
  lockDrawer: () => void;
  unlockDrawer: () => void;
  openDrawer: () => void;
  closeDrawer: () => void;
  toggleDrawer: () => void;
  setOpen?: SetState<boolean>;
}

export const useDrawer = (initialState: Partial<DrawerState> = { open: false, locked: false }) => {
  const { open = false, locked = false } = initialState;
  const [isDrawerOpen, setDrawerOpen] = useState(open);
  const [isDrawerLocked, setDrawerLocked] = useState(locked);

  const latestOpen = useLatest(isDrawerOpen);
  const latestLocked = useLatest(isDrawerLocked);

  const lockDrawer = useCallback(() => {
    setDrawerLocked(true);
    setDrawerOpen(true);
  }, []);

  const unlockDrawer = useCallback(() => {
    setDrawerLocked(false);
  }, []);

  const openDrawer = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    if (!latestLocked.current) {
      setDrawerOpen(false);
    }
  }, [latestLocked]);

  const setOpen: SetState<boolean> = useCallback((open) => {
    if (typeof open === 'boolean') {
      return setDrawerOpen(open);
    }
    setDrawerOpen((prev) => open(prev));
  }, []);

  const toggleDrawer = useCallback(() => {
    if (latestOpen.current) {
      closeDrawer();
    } else {
      openDrawer();
    }
  }, [closeDrawer, latestOpen, openDrawer]);

  const drawerState = useMemo(() => {
    return { open: isDrawerOpen, locked: isDrawerLocked };
  }, [isDrawerLocked, isDrawerOpen]);

  const drawerControls: DrawerControls = useMemo(() => {
    return { lockDrawer, unlockDrawer, openDrawer, closeDrawer, toggleDrawer, setOpen };
  }, [closeDrawer, lockDrawer, openDrawer, toggleDrawer, unlockDrawer, setOpen]);

  return [drawerState, drawerControls] as const;
};
